import React, { useContext, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPaperPlane,
  faEye,
  faEyeSlash,
  faChevronLeft,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { AppContext } from "../context/appContext";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as yup from "yup";
import * as func from "../../api/apiHelper";
import * as urls from "../../api/urlHelper";
import Select from "react-select";
import { MODULE_NAMES, STAFF_BELONGS_TO } from "../../config/enum";
import { useParams } from "react-router-dom";

const STAFF_TYPES = ["Admin", "Staff"];

const inputCls =
  "w-full text-sm mt-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500";

const AddStaffAccount = ({ type }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setAppData } = useContext(AppContext);

  const [showPassword, setShowPassword] = useState(false);
  const [disableSubmitButton, setDisableSubmitButton] = useState(false);

  const validationSchema = (type) =>
    yup.object({
      name: yup
        .string()
        .required("Name is required")
        .test("no-whitespace-only", "Name cannot be empty or contain only spaces", (v) => v && v.trim().length > 0)
        .min(3, "Name must be at least 3 characters")
        .max(30, "Name cannot exceed 30 characters"),
      emailId: yup
        .string()
        .required("Email is required")
        .test("no-whitespace-only", "Email cannot contain only spaces", (v) => v && v.trim().length > 0)
        .email("Invalid email format")
        .max(40, "Email cannot exceed 40 characters")
        .test("valid-length", "Email must not exceed 40 characters after trimming", (v) => v && v.trim().length <= 40),
      password: yup.string().when([], {
        is: () => type !== "edit",
        then: (schema) =>
          schema
            .min(8, "Min 8 chars")
            .matches(/[A-Z]/, "Must have 1 uppercase")
            .matches(/[a-z]/, "Must have 1 lowercase")
            .matches(/\d/, "Must have 1 digit")
            .matches(/[,]/, "Must have 1 comma")
            .matches(/[\W_]/, "Must have 1 special char")
            .test("no-sequential-characters", "No more than 5 same chars", (value) => {
              const sequences = /([a-zA-Z0-9])\1{5}/;
              return !sequences.test(value);
            })
            .test("no-ascending-or-descending-sequences", "No sequences like abcdef or 123456", (value) => {
              const hasSequentialChars = (str) => {
                for (let i = 0; i < str?.length - 1; i++) {
                  if (str.charCodeAt(i) + 1 !== str.charCodeAt(i + 1)) return false;
                }
                return true;
              };
              const hasDescendingChars = (str) => {
                for (let i = 0; i < str?.length - 1; i++) {
                  if (str.charCodeAt(i) - 1 !== str.charCodeAt(i + 1)) return false;
                }
                return true;
              };
              for (let i = 0; i <= value?.length - 6; i++) {
                const substring = value.slice(i, i + 6);
                if (hasSequentialChars(substring) || hasDescendingChars(substring)) return false;
              }
              return true;
            })
            .test("comma-not-at-start-or-end", "Comma must not be at the start or end", (value) => {
              const length = value.length;
              const commaIndex = value.indexOf(",");
              return commaIndex > 0 && commaIndex < length - 1;
            })
            .required("Password is required"),
        otherwise: (schema) => schema.notRequired(),
      }),
      staffType: yup.string().required("Staff type is required"),
      accessModules: yup.array().when("staffType", {
        is: (value) => value === "Staff",
        then: (schema) => schema.min(1, "Select at least one module").required("Access modules are required"),
        otherwise: (schema) => schema.optional(),
      }),
      staffBelongsTo: yup.object().when("staffType", {
        is: (value) => value === "Staff",
        then: (schema) => schema.required("Staff Belongs To is required"),
        otherwise: (schema) => schema.optional().nullable(),
      }),
      status: yup.string().required("Status is required"),
    });

  const formik = useFormik({
    initialValues: {
      name: "",
      emailId: "",
      password: "",
      staffType: "",
      accessModules: [],
      staffBelongsTo: "",
      status: "Active",
    },
    validationSchema: validationSchema(type),
    enableReinitialize: true,
    onSubmit: async (values) => {
      setDisableSubmitButton(true);
      const apiBody = {
        ...values,
        accessModules: values.accessModules.map((each) => each.value),
        staffBelongsTo: values.staffBelongsTo?.value,
      };

      if (type === "edit") {
        const editApiResponse = await func.put(urls.EDIT_STAFF_URL + id, {
          ...apiBody,
          staffBelongsTo: apiBody.staffType === "Admin" ? null : apiBody.staffBelongsTo,
        });
        if (editApiResponse.status === 200) {
          toast.success("Staff Updated Successfully");
          navigate("/staff-access-management/list");
        } else {
          toast.warn(editApiResponse);
        }
      } else {
        const createapiResponse = await func.post(urls.CREATE_STAFF_URL, apiBody);
        if (createapiResponse.status === 201) {
          toast.success("Staff Created Successfully");
          navigate("/staff-access-management/list");
        } else {
          toast.warn(createapiResponse);
        }
      }

      setDisableSubmitButton(false);
    },
  });

  useEffect(() => {
    const getStaffDetails = async () => {
      const staffDetailsApiResponse = await func.get(urls.GET_STAFF_DETAILS_BY_ID + id);
      if (staffDetailsApiResponse.status === 200) {
        const finalResponse = {
          ...staffDetailsApiResponse.data.data,
          password: "",
          staffBelongsTo:
            staffDetailsApiResponse.data.data.staffBelongsTo !== null
              ? {
                  label: staffDetailsApiResponse.data.data.staffBelongsTo,
                  value: staffDetailsApiResponse.data.data.staffBelongsTo,
                }
              : null,
          accessModules: staffDetailsApiResponse.data.data.accessModules.map((Each) => ({
            label: Each,
            value: Each,
          })),
        };
        formik.setValues(finalResponse);
      }
      if (staffDetailsApiResponse.status === 403) {
        toast.error(staffDetailsApiResponse);
      }
    };

    if (id) getStaffDetails();
  }, [id, type]);

  return (
    <div className="min-h-screen bg-[linear-gradient(to_left,_#19224B,_#E2B865)] p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-8 h-8 flex items-center justify-center bg-white/20 hover:bg-white/30 rounded-lg transition text-white"
          >
            <FontAwesomeIcon icon={faChevronLeft} className="text-sm" />
          </button>
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faUsers} className="text-white text-lg" />
            <h1 className="text-lg font-semibold text-white">
              {type === "edit" ? "Edit" : "Add"} Staff
            </h1>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm p-6">
          <form onSubmit={formik.handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="Enter Name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  onInput={(e) => {
                    let value = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                    if (value.trim() === "") value = "";
                    e.target.value = value;
                  }}
                  className={inputCls}
                />
                {formik.touched.name && formik.errors.name && (
                  <p className="text-red-500 text-xs mt-1">{formik.errors.name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="emailId"
                  placeholder="Enter Email Id"
                  value={formik.values.emailId}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={inputCls}
                />
                {formik.touched.emailId && formik.errors.emailId && (
                  <p className="text-red-500 text-xs mt-1">{formik.errors.emailId}</p>
                )}
              </div>

              {/* Password */}
              {type !== "edit" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Password <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      placeholder="Enter Password"
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={inputCls}
                    />
                    <span
                      className="cursor-pointer absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </span>
                  </div>
                  {formik.touched.password && formik.errors.password && (
                    <p className="text-red-500 text-xs mt-1">{formik.errors.password}</p>
                  )}
                </div>
              )}

              {/* Staff Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Staff Type <span className="text-red-500">*</span>
                </label>
                <select
                  name="staffType"
                  value={formik.values.staffType}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={inputCls}
                >
                  <option value="">Select Staff Type</option>
                  {STAFF_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
                {formik.touched.staffType && formik.errors.staffType && (
                  <p className="text-red-500 text-xs mt-1">{formik.errors.staffType}</p>
                )}
              </div>

              {/* Staff Belongs To (conditional) */}
              {formik.values.staffType === "Staff" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Staff Belongs To <span className="text-red-500">*</span>
                  </label>
                  <Select
                    name="staffBelongsTo"
                    placeholder="Select Staff Belongs To"
                    value={formik.values.staffBelongsTo || null}
                    onChange={(e) => formik.setFieldValue("staffBelongsTo", e)}
                    options={STAFF_BELONGS_TO.map((each) => ({ label: each, value: each }))}
                    className="mt-1 text-sm"
                    onBlur={formik.handleBlur}
                  />
                  {(formik.touched.staffBelongsTo || formik.errors.staffBelongsTo) && (
                    <p className="text-red-500 text-xs mt-1">{formik.errors.staffBelongsTo}</p>
                  )}
                </div>
              )}

              {/* Access Modules (conditional) */}
              {formik.values.staffType === "Staff" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Access Modules <span className="text-red-500">*</span>
                  </label>
                  <Select
                    name="accessModules"
                    isMulti
                    value={formik.values.accessModules}
                    onChange={(e) => formik.setFieldValue("accessModules", e)}
                    options={MODULE_NAMES.map((each) => ({ label: each, value: each }))}
                    className="mt-1 text-sm"
                  />
                  {formik.touched.accessModules && formik.errors.accessModules && (
                    <p className="text-red-500 text-xs mt-1">{formik.errors.accessModules}</p>
                  )}
                </div>
              )}

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  name="status"
                  value={formik.values.status}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={inputCls}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>
            </div>

            <p className="text-xs text-gray-500">
              <span className="text-red-500">*</span> Represents required fields
            </p>

            <div className="flex justify-end">
              <button
                type="submit"
                disabled={disableSubmitButton}
                className="flex items-center gap-2 px-6 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-md text-sm font-medium transition"
              >
                <FontAwesomeIcon icon={faPaperPlane} />
                {type === "edit" ? "Update" : "Add"} Staff
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddStaffAccount;
